import { TZDate } from "@date-fns/tz";
import { format, formatDistanceToNowStrict } from "date-fns";

/** All calendar math happens in the user's chosen timezone. */
export function inTz(date: Date | string | number, tz: string) {
  return new TZDate(new Date(date).getTime(), tz);
}

export function fmt(date: Date | string | number, tz: string, pattern = "MMM d, yyyy 'at' HH:mm") {
  return format(inTz(date, tz), pattern);
}

export function relative(date: Date | string) {
  return formatDistanceToNowStrict(new Date(date), { addSuffix: true });
}

/** Builds an instant from a wall-clock date ("yyyy-MM-dd") and time ("HH:mm") in tz. */
export function fromWallClock(day: string, time: string, tz: string): Date {
  const [y, m, d] = day.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);

  return new Date(new TZDate(y ?? 1970, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, tz).getTime());
}

export function toWallClock(date: Date | string, tz: string) {
  const z = inTz(date, tz);

  return { day: format(z, "yyyy-MM-dd"), time: format(z, "HH:mm") };
}

export function minutesToLabel(minutes: number) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;

  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function labelToMinutes(label: string) {
  const [h, m] = label.split(":").map(Number);

  return (h ?? 0) * 60 + (m ?? 0);
}

export const TIMEZONES: string[] = (() => {
  try {
    return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf("timeZone");
  } catch {
    return ["UTC", "Europe/London", "America/New_York", "Africa/Addis_Ababa", "Asia/Tokyo"];
  }
})();

/** "Addis Ababa (GMT+3)" for "Africa/Addis_Ababa": readable, with the offset. */
export function tzLabel(tz: string, at: Date = new Date()) {
  if (tz === "UTC" || tz === "Etc/UTC") return "UTC";
  const city = (tz.split("/").pop() ?? tz).replace(/_/g, " ");
  try {
    const offset = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName")?.value;

    return offset ? `${city} (${offset})` : city;
  } catch {
    return city;
  }
}
